import {FC, useState, useEffect, useRef} from 'react'
import {PageTitle} from '_metronic/layout/core'
import {laporanKegiatan, aktivitasPenugasan} from '../../../services/pana'
import Api from '../../../thesis-and-dissertations/helpers/api'
import '../../styles/pejuang.css'

const api = new Api()

interface LaporanFile {
  id: number
  file_name: string
  file_path: string
  file_url?: string
}

interface LaporanKegiatan {
  id?: number
  pendaftaran_pana_id?: number
  judul?: string
  title?: string // Alternative field name
  deskripsi?: string
  description?: string // Alternative field name
  link?: string
  url?: string // Alternative field name
  minggu_ke: number
  files?: LaporanFile[]
  attachments?: LaporanFile[] // Alternative field name
  /** API: 0=menunggu_review, 1=disetujui, 2=dikembalikan */
  status?: number | string | 'submitted' | 'in_review' | 'approved' | 'revision'
  catatan?: string // Catatan dari admin (primary)
  catatan_revisi?: string // Catatan revisi dari admin
  review_note?: string // Alternative field name
  reviewed_by?: string // Nama admin yang review
  reviewer_name?: string // Alternative field name
  reviewed_at?: string // Tanggal review
  created_at?: string
  updated_at?: string
  submitted_at?: string // Alias tanggal submit dari beberapa endpoint
  status_label?: string // Label status dari API (jika ada)
}

interface AktivitasPenugasan {
  id: number
  minggu_ke: number
  aktivitas: string
  strategi: string
  urutan: number
  is_active: number
}

interface MonevWeek {
  minggu_ke: number
  laporan: LaporanKegiatan | null
  aktivitas: AktivitasPenugasan[] | null
  status: 'completed' | 'active' | 'locked' | 'pending_review' | 'revision'
}

function pickFirstNonEmptyString(
  ...vals: Array<string | undefined | null>
): string | undefined {
  for (const v of vals) {
    if (v != null && String(v).trim() !== '') return String(v)
  }
  return undefined
}

function formatLaporanTanggalId(dateStr: string | undefined | null): string {
  if (!dateStr) return '-'
  const d = new Date(dateStr)
  return Number.isNaN(d.getTime())
    ? '-'
    : d.toLocaleDateString('id-ID', {day: 'numeric', month: 'short', year: 'numeric'})
}

function formatLaporanPengajuanId(id: unknown): string {
  if (id == null || String(id).trim() === '') return '-'
  return `#${id}`
}

/** Backend: 0=menunggu_review, 1=disetujui, 2=dikembalikan */
function getLaporanReviewStatusLabel(laporan: LaporanKegiatan): string {
  const raw = laporan.status
  if (raw !== undefined && raw !== null && String(raw).trim() !== '') {
    const n = typeof raw === 'number' ? raw : Number(raw)
    if (!Number.isNaN(n)) {
      switch (n) {
        case 0:
          return 'Menunggu Review'
        case 1:
          return 'Disetujui'
        case 2:
          return 'Dikembalikan'
        default:
          break
      }
    }
  }
  if (laporan.status_label) return laporan.status_label
  return '-'
}

/** Map my-laporan-grouped item + file shapes (path vs file_path) into UI model */
function normalizeLaporanFromGroupedApi(raw: any, mingguKe: number): LaporanKegiatan {
  const createdAt = pickFirstNonEmptyString(
    raw.created_at,
    raw.submitted_at,
    raw.createdAt,
    raw.submittedAt
  )
  const reviewedAt = pickFirstNonEmptyString(
    raw.reviewed_at,
    raw.reviewedAt,
    raw.approved_at,
    raw.approvedAt
  )
  const statusLabel = pickFirstNonEmptyString(raw.status_label, raw.status_text, raw.status_nama)

  const base: LaporanKegiatan = {
    ...raw,
    minggu_ke: raw.minggu_ke ?? mingguKe,
    ...(createdAt ? {created_at: createdAt} : {}),
    ...(reviewedAt ? {reviewed_at: reviewedAt} : {}),
    ...(statusLabel ? {status_label: statusLabel} : {}),
  }
  const filesRaw = raw.files ?? raw.attachments
  if (Array.isArray(filesRaw)) {
    base.files = filesRaw.map((f: any) => ({
      id: f.id,
      file_name:
        f.file_name ??
        f.name ??
        (typeof f.path === 'string' ? f.path.split('/').filter(Boolean).pop() || '' : ''),
      file_path: f.file_path ?? f.path ?? '',
      file_url: f.file_url ?? f.url,
    }))
  }
  return base
}

const MonevPage: FC = () => {
  const fileInputRef = useRef<HTMLInputElement>(null)
  
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [hasAccess, setHasAccess] = useState<boolean | null>(null) // null = belum cek, true = boleh akses, false = tidak boleh
  const [monevData, setMonevData] = useState<MonevWeek[]>([])
  const [expandedWeek, setExpandedWeek] = useState<number | null>(null)
  const [pendaftaranPanaId, setPendaftaranPanaId] = useState<number | null>(null)
  const [totalWeeks, setTotalWeeks] = useState(1) // Default 1 minggu
  const [laporanCache, setLaporanCache] = useState<Record<number, LaporanKegiatan | null>>({})
  const [formData, setFormData] = useState({
    judul: '',
    deskripsi: '',
    link: '',
    files: [] as File[],
  })
  const [editingLaporanId, setEditingLaporanId] = useState<number | null>(null)

  // Generate weeks data based on totalWeeks
  const generateWeeksData = (aktivitasGrouped: any, maxWeeks: number, cacheData?: Record<number, any>) => {
    // Use cache parameter if provided, otherwise use state
    const cache = cacheData || laporanCache
    
    const weeks: MonevWeek[] = []
    
    for (let i = 1; i <= maxWeeks; i++) {
      const laporan = cache[i] || null
      const aktivitas = aktivitasGrouped?.[i] || null
      let status: 'completed' | 'active' | 'locked' | 'pending_review' | 'revision' = 'active'
      
      if (laporan) {
        // Cek status review dari laporan
        // status: 0=pending, 1=approved, 2=rejected/revision
        const statusNum = typeof laporan.status === 'number' ? laporan.status : Number(laporan.status)
        
        if (statusNum == 1) {
          status = 'completed'
        } else if (statusNum == 2) {
          status = 'revision'
        } else {
          // status 0 = pending review
          status = 'pending_review'
        }
      } else {
        // Tidak ada laporan di minggu ini -> active (bisa diisi)
        status = 'active'
      }
      
      weeks.push({
        minggu_ke: i,
        laporan,
        aktivitas,
        status,
      })
    }
    
    return weeks
  }

  // Fetch data
  const fetchData = async () => {
    try {
      setLoading(true)
      
      // Get user's pendaftaran PANA first (untuk ambil pendaftaran_pana_id dan cek status)
      if (!pendaftaranPanaId) {
        try {
          const panaResponse: any = await api.getRegisterPana()
          const panaData = panaResponse?.data?.data
          
          // Cek status_penilaian - hanya "Lolos" yang boleh akses
          if (panaData?.status_penilaian !== 'Lolos') {
            setHasAccess(false)
            setLoading(false)
            return
          }
          
          setHasAccess(true)
          
          if (panaData?.id) {
            setPendaftaranPanaId(panaData.id)
          }
        } catch (error) {
          // Error fetching pendaftaran PANA
          setHasAccess(false)
          setLoading(false)
          return
        }
      }
      
      // Fetch laporan grouped by minggu (GET .../my-laporan-grouped) — keep full payload for UI
      let laporanList: Record<number, LaporanKegiatan> = {}
      try {
        const laporanResponse: any = await laporanKegiatan.getGroupedByMinggu()
        
        const apiData = laporanResponse.data?.data || laporanResponse.data
        
        if (apiData && typeof apiData === 'object') {
          Object.keys(apiData).forEach((key) => {
            // Skip non-numeric keys (meta, message, etc)
            if (isNaN(Number(key))) return
            
            const mingguKe = Number(key)
            const laporanArray = apiData[key]
            
            // Take first item from array (latest submission)
            if (Array.isArray(laporanArray) && laporanArray.length > 0) {
              laporanList[mingguKe] = normalizeLaporanFromGroupedApi(laporanArray[0], mingguKe)
            }
          })
        }
      } catch (error) {
        // Error fetching laporan grouped
      }
      
      // Fetch aktivitas grouped (single API call - more efficient than loop)
      let calculatedTotalWeeks = 1
      let aktivitasGrouped: Record<number, AktivitasPenugasan[]> = {}
      
      try {
        const aktivitasResponse: any = await aktivitasPenugasan.getGroupedByMinggu()
        
        const apiData = aktivitasResponse.data?.data || aktivitasResponse.data
        
        if (apiData && typeof apiData === 'object') {
          Object.keys(apiData).forEach((key) => {
            // Skip non-numeric keys
            if (isNaN(Number(key))) return
            
            const mingguKe = Number(key)
            const aktivitasArray = apiData[key]
            
            if (Array.isArray(aktivitasArray) && aktivitasArray.length > 0) {
              aktivitasGrouped[mingguKe] = aktivitasArray
              // Track highest week number
              if (mingguKe > calculatedTotalWeeks) {
                calculatedTotalWeeks = mingguKe
              }
            }
          })
        }
      } catch (error) {
        // Error fetching aktivitas grouped
      }
      
      // Cache = full laporan per minggu from my-laporan-grouped (judul, deskripsi, link, files, dll.)
      const initialCache: Record<number, LaporanKegiatan> = {...laporanList}
      setLaporanCache(initialCache)
      
      // Calculate total weeks to display: 
      // - All weeks that have laporan
      // - Plus 1 next week for input (if not at max)
      let displayTotalWeeks = 1 // Default at least 1 week
      
      const laporanMingguKeys = Object.keys(laporanList).map(Number).sort((a, b) => a - b)
      if (laporanMingguKeys.length > 0) {
        const maxLaporanMinggu = Math.max(...laporanMingguKeys)
        // Show max laporan week + 1 (for next input), but don't exceed calculatedTotalWeeks
        displayTotalWeeks = Math.min(maxLaporanMinggu + 1, calculatedTotalWeeks)
      }
      
      setTotalWeeks(displayTotalWeeks)
      setMonevData(generateWeeksData(aktivitasGrouped, displayTotalWeeks, initialCache))
    } catch (error) {
      // Fallback to 1 week
      setTotalWeeks(1)
      setMonevData(generateWeeksData({}, 1, {}))
    } finally {
      setLoading(false)
    }
  }

  // Fetch laporan detail for specific week
  const fetchLaporanByMinggu = async (mingguKe: number) => {
    // Check if already have full detail (not just basic info)
    const cached = laporanCache[mingguKe]
    if (cached && cached.deskripsi !== undefined) {
      return
    }
    
    // If cache is null (no laporan), don't fetch
    if (cached === null) {
      return
    }
    
    try {
      const response: any = await laporanKegiatan.getList({minggu_ke: mingguKe})
      
      const apiData = response.data?.data || response.data
      let laporan: LaporanKegiatan | null = null
      
      if (apiData && Array.isArray(apiData) && apiData.length > 0) {
        laporan = normalizeLaporanFromGroupedApi(apiData[0], mingguKe)
      }
      
      // Update cache
      setLaporanCache(prev => ({...prev, [mingguKe]: laporan}))
      
      // Update monevData with laporan detail (keep existing status, don't override)
      setMonevData(prev => prev.map(week => {
        if (week.minggu_ke == mingguKe && laporan) {
          return {
            ...week,
            laporan: laporan, // Update with full detail
            // Keep status as is - don't override
          }
        }
        return week
      }))
    } catch (error) {
      // Cache null to prevent repeated failed requests
      setLaporanCache(prev => ({...prev, [mingguKe]: null}))
    }
  }

  useEffect(() => {
    // Reset state sebelum fetch data baru
    setMonevData([])
    setLaporanCache({})
    setPendaftaranPanaId(null)
    setExpandedWeek(null)
    setTotalWeeks(1)
    
    fetchData()
    
    // Cleanup: reset state saat unmount (ganti akun/logout)
    return () => {
      setMonevData([])
      setLaporanCache({})
      setPendaftaranPanaId(null)
      setExpandedWeek(null)
      setTotalWeeks(1)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [localStorage.getItem('apiToken')]) // Re-run saat token berubah
  
  // Fetch laporan when week is expanded
  useEffect(() => {
    if (expandedWeek !== null) {
      fetchLaporanByMinggu(expandedWeek)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [expandedWeek])

  const completedWeeks = monevData.filter((m) => m.status == 'completed').length
  const pendingReviewWeeks = monevData.filter((m) => m.status == 'pending_review').length
  const revisionWeeks = monevData.filter((m) => m.status == 'revision').length
  const progress = (completedWeeks / 12) * 100 // Total fixed 12 minggu

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'completed':
        return (
          <div className='rounded-circle d-flex align-items-center justify-content-center' style={{width: '40px', height: '40px', background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'}}>
            <i className='bi bi-check-lg text-white'></i>
          </div>
        )
      case 'active':
        return (
          <div className='rounded-circle d-flex align-items-center justify-content-center' style={{width: '40px', height: '40px', background: 'linear-gradient(135deg, #F95E2F 0%, #FDC830 100%)', animation: 'pulse 2s infinite'}}>
            <i className='bi bi-pencil text-white'></i>
          </div>
        )
      case 'pending_review':
        return (
          <div className='rounded-circle d-flex align-items-center justify-content-center' style={{width: '40px', height: '40px', background: 'linear-gradient(135deg, #0d6efd 0%, #6610f2 100%)'}}>
            <i className='bi bi-hourglass-split text-white'></i>
          </div>
        )
      case 'revision':
        return (
          <div className='rounded-circle d-flex align-items-center justify-content-center' style={{width: '40px', height: '40px', background: 'linear-gradient(135deg, #dc3545 0%, #fd7e14 100%)', animation: 'pulse 2s infinite'}}>
            <i className='bi bi-exclamation-triangle text-white'></i>
          </div>
        )
      case 'locked':
        return (
          <div className='rounded-circle d-flex align-items-center justify-content-center' style={{width: '40px', height: '40px', background: '#e9ecef'}}>
            <i className='bi bi-lock text-secondary'></i>
          </div>
        )
      default:
        return null
    }
  }

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'completed':
        return <span className='badge bg-success px-3 py-2'>Disetujui</span>
      case 'active':
        return <span className='badge bg-warning px-3 py-2'>Belum Diisi</span>
      case 'pending_review':
        return <span className='badge bg-primary px-3 py-2'>Menunggu Review</span>
      case 'revision':
        return <span className='badge bg-danger px-3 py-2'>Perlu Revisi</span>
      case 'locked':
        return <span className='badge bg-secondary px-3 py-2'>Terkunci</span>
      default:
        return null
    }
  }

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const newFiles = Array.from(e.target.files)
      setFormData({...formData, files: [...formData.files, ...newFiles]})
    }
  }

  const removeFile = (index: number) => {
    const newFiles = formData.files.filter((_, i) => i !== index)
    setFormData({...formData, files: newFiles})
  }

  const handleSubmit = async (mingguKe: number, laporanId?: number) => {
    if (!formData.judul.trim()) {
      alert('Judul laporan harus diisi!')
      return
    }

    if (!formData.deskripsi.trim()) {
      alert('Deskripsi kegiatan harus diisi!')
      return
    }

    if (formData.deskripsi.trim().length < 2000) {
      alert(`Deskripsi kegiatan minimal 2000 karakter! Saat ini: ${formData.deskripsi.trim().length} karakter`)
      return
    }

    if (!formData.link.trim()) {
      alert('Link dokumentasi harus diisi!')
      return
    }

    try {
      setSubmitting(true)
      
      if (!pendaftaranPanaId) {
        alert('ID Pendaftaran PANA tidak ditemukan. Silakan muat ulang halaman.')
        return
      }

      const payload: any = {
        pendaftaran_pana_id: pendaftaranPanaId,
        judul: formData.judul,
        deskripsi: formData.deskripsi,
        link: formData.link,
        minggu_ke: mingguKe,
        files: formData.files,
      }

      let response: any
      if (laporanId) {
        // Update existing laporan (revisi)
        response = await laporanKegiatan.update(laporanId, payload)
        alert('Revisi laporan berhasil disubmit!')
      } else {
        // Create new laporan
        response = await laporanKegiatan.create(payload)
        alert('Laporan berhasil disimpan!')
      }
      
      // Update UI optimistically - set current week as pending_review
      const updatedMonevData = monevData.map((week) => {
        if (week.minggu_ke == mingguKe) {
          return {
            ...week,
            status: 'pending_review' as const,
            laporan: {
              ...week.laporan,
              id: response?.data?.data?.id || week.laporan?.id,
              judul: formData.judul,
              deskripsi: formData.deskripsi,
              link: formData.link,
              minggu_ke: mingguKe,
              status: 0, // pending
            } as any,
            aktivitas: week.aktivitas // Keep existing aktivitas
          }
        }
        return week
      })
      setMonevData(updatedMonevData)
      
      // Reset form
      setFormData({judul: '', deskripsi: '', link: '', files: []})
      setEditingLaporanId(null)
      
      // Collapse current week
      setExpandedWeek(null)
    } catch (error: any) {
      alert(error.response?.data?.message || 'Gagal menyimpan laporan. Silakan coba lagi.')
    } finally {
      setSubmitting(false)
    }
  }
  
  // Load form data for revision
  const loadFormForRevision = (laporan: LaporanKegiatan) => {
    setFormData({
      judul: laporan.judul || laporan.title || '',
      deskripsi: laporan.deskripsi || laporan.description || '',
      link: laporan.link || laporan.url || '',
      files: [],
    })
    setEditingLaporanId(laporan.id || null)
  }

  // Tambah minggu baru
  const handleAddWeek = () => {
    const newWeekNumber = totalWeeks + 1
    setTotalWeeks(newWeekNumber)
    
    // Add new week to monevData
    const newWeek: MonevWeek = {
      minggu_ke: newWeekNumber,
      laporan: null,
      aktivitas: null,
      status: 'active',
    }
    
    // Tambah minggu baru tanpa mengunci minggu sebelumnya
    // Minggu yang belum submit tetap bisa diisi
    setMonevData([...monevData, newWeek])
    setExpandedWeek(newWeekNumber)
    
    // Reset form
    setFormData({judul: '', deskripsi: '', link: '', files: []})
  }
  
  // Cek apakah bisa tambah minggu (minggu terakhir harus sudah submit)
  const canAddWeek = () => {
    if (monevData.length == 0) return false
    
    // Cek minggu terakhir - harus BUKAN active (artinya sudah submit)
    const lastWeek = monevData[monevData.length - 1]
    
    // Tombol TIDAK muncul jika:
    // 1. Minggu terakhir masih active (belum submit)
    // 2. Tidak ada data monev
    
    // Tombol MUNCUL jika:
    // Status minggu terakhir adalah pending_review, completed, atau revision
    // (artinya sudah pernah submit laporan)
    const canAdd = lastWeek.status !== 'active' && lastWeek.status !== 'locked'
    
    return canAdd
  }

  if (loading) {
    return (
      <div className='d-flex justify-content-center align-items-center py-5'>
        <div className='spinner-border text-primary' role='status'>
          <span className='visually-hidden'>Loading...</span>
        </div>
      </div>
    )
  }

  // Jika tidak punya akses (status bukan "Lolos"), jangan tampilkan halaman
  if (hasAccess == false) {
    return null
  }

  return (
    <div className='col-md-12'>
      {/* Hero Header */}
      <div className='pejuang-hero text-white mb-5'>
        <div className='row align-items-center'>
          <div className='col-lg-8'>
            <h2 className='mb-2'>Monitoring & Evaluasi Mingguan</h2>
            <p className='mb-0 opacity-90'>Laporan kegiatan mingguan selama 12 minggu penugasan</p>
          </div>
          <div className='col-lg-4 text-lg-end mt-3 mt-lg-0'>
            <div className='d-inline-flex align-items-center bg-white bg-opacity-20 rounded-pill px-4 py-2'>
              <i className='bi bi-calendar-check me-2'></i>
              <span>Total 12 Minggu</span>
            </div>
          </div>
        </div>
      </div>

      {/* Statistics Cards */}
      <div className='row mb-5'>
        <div className='col-md-3 mb-4'>
          <div className='pejuang-stat-card'>
            <div className='stat-icon' style={{background: 'linear-gradient(135deg, #28a745 0%, #20c997 100%)'}}>
              <i className='bi bi-check-circle text-white'></i>
            </div>
            <div className='stat-value text-success'>{completedWeeks}</div>
            <div className='stat-label'>Disetujui</div>
          </div>
        </div>
        <div className='col-md-3 mb-4'>
          <div className='pejuang-stat-card'>
            <div className='stat-icon' style={{background: 'linear-gradient(135deg, #0d6efd 0%, #6610f2 100%)'}}>
              <i className='bi bi-hourglass-split text-white'></i>
            </div>
            <div className='stat-value text-primary'>{pendingReviewWeeks}</div>
            <div className='stat-label'>Menunggu Review</div>
          </div>
        </div>
        <div className='col-md-3 mb-4'>
          <div className='pejuang-stat-card'>
            <div className='stat-icon' style={{background: 'linear-gradient(135deg, #dc3545 0%, #fd7e14 100%)'}}>
              <i className='bi bi-exclamation-triangle text-white'></i>
            </div>
            <div className='stat-value text-danger'>{revisionWeeks}</div>
            <div className='stat-label'>Perlu Revisi</div>
          </div>
        </div>
        <div className='col-md-3 mb-4'>
          <div className='pejuang-stat-card'>
            <div className='stat-icon' style={{background: 'linear-gradient(135deg, #F95E2F 0%, #FDC830 100%)'}}>
              <i className='bi bi-percent text-white'></i>
            </div>
            <div className='stat-value' style={{color: '#F95E2F'}}>{Math.round(progress)}%</div>
            <div className='stat-label'>Progress</div>
          </div>
        </div>
      </div>

      {/* Progress Card */}
      <div className='pejuang-card mb-5'>
        <div className='card-body p-4'>
          <div className='d-flex justify-content-between align-items-center mb-3'>
            <div>
              <h6 className='fw-bold mb-1'>Progress Penugasan</h6>
              <small className='text-muted'>Total 12 laporan mingguan</small>
            </div>
            <span className='badge bg-primary fs-6 px-4 py-2'>
              {completedWeeks} / 12 Minggu
            </span>
          </div>
          <div className='pejuang-progress' style={{height: '15px'}}>
            <div className='pejuang-progress-bar' style={{width: `${progress}%`}}></div>
          </div>
          <div className='d-flex justify-content-between mt-2'>
            <small className='text-muted'>Mulai</small>
            <small className='text-muted'>Selesai</small>
          </div>
        </div>
      </div>

      {/* Monev Timeline */}
      <div className='pejuang-card'>
        <div className='card-header d-flex justify-content-between align-items-center'>
          <h5 className='mb-0'>
            <i className='bi bi-calendar-week'></i>
            Daftar Laporan Mingguan
          </h5>
          {canAddWeek() && (
            <button
              className='btn btn-pejuang'
              onClick={handleAddWeek}
            >
              <i className='bi bi-plus-lg me-2'></i>
              Tambah Minggu {totalWeeks + 1}
            </button>
          )}
        </div>
        <div className='card-body p-0'>
          {monevData.map((monev) => (
            <div 
              key={monev.minggu_ke} 
              className={`border-bottom ${monev.status == 'locked' ? 'opacity-50' : ''}`}
            >
              {/* Week Header */}
              <div
                className={`p-4 d-flex justify-content-between align-items-center ${expandedWeek == monev.minggu_ke ? 'bg-light' : ''}`}
                style={{cursor: monev.status !== 'locked' ? 'pointer' : 'default'}}
                onClick={() => {
                  if (monev.status !== 'locked') {
                    setExpandedWeek(expandedWeek == monev.minggu_ke ? null : monev.minggu_ke)
                  }
                }}
              >
                <div className='d-flex align-items-center'>
                  {getStatusIcon(monev.status)}
                  <div className='ms-3'>
                    <h6 className='fw-bold mb-0'>Minggu {monev.minggu_ke}</h6>
                    {(monev.laporan?.judul || monev.laporan?.title) && (
                      <small className='text-muted'>{monev.laporan.judul || monev.laporan.title}</small>
                    )}
                  </div>
                </div>
                <div className='d-flex align-items-center gap-3'>
                  {getStatusBadge(monev.status)}
                  {monev.status !== 'locked' && (
                    <i className={`bi bi-chevron-${expandedWeek == monev.minggu_ke ? 'up' : 'down'} fs-5`}></i>
                  )}
                </div>
              </div>

              {/* Week Content */}
              {expandedWeek == monev.minggu_ke && monev.status !== 'locked' && (
                <div className='p-4 pt-0'>
                  {/* AKTIVITAS & STRATEGI PENUGASAN */}
                  {monev.aktivitas && monev.aktivitas.length > 0 && (
                    <div className='mb-4 p-4 rounded-3' style={{background: '#e7f3ff', border: '2px solid #0d6efd'}}>
                      <h6 className='fw-bold mb-3 d-flex align-items-center' style={{color: '#0d6efd'}}>
                        <i className='bi bi-clipboard-check me-2'></i>
                        Aktivitas & Strategi Minggu {monev.minggu_ke}
                      </h6>
                      {monev.aktivitas.map((akt, index) => (
                        <div key={akt.id} className='mb-3 p-3 bg-white rounded'>
                          <div className='d-flex align-items-start mb-2'>
                            <div className='rounded-circle bg-primary text-white d-flex align-items-center justify-content-center me-2' style={{width: '24px', height: '24px', fontSize: '12px'}}>
                              {index + 1}
                            </div>
                            <div className='flex-grow-1'>
                              <div className='fw-bold mb-1' style={{color: '#0d6efd'}}>
                                <i className='bi bi-bullseye me-1'></i>Aktivitas:
                              </div>
                              <p className='mb-2'>{akt.aktivitas}</p>
                              <div className='fw-bold mb-1' style={{color: '#198754'}}>
                                <i className='bi bi-lightbulb me-1'></i>Strategi:
                              </div>
                              <p className='mb-0'>{akt.strategi}</p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                  
                  {/* COMPLETED - Laporan sudah disetujui */}
                  {monev.status == 'completed' && monev.laporan ? (
                    <div className='p-4 rounded-3' style={{background: '#d4edda'}}>
                      <h6 className='fw-bold mb-3 d-flex align-items-center'>
                        <i className='bi bi-check-circle-fill me-2 text-success'></i>
                        Laporan Disetujui
                      </h6>
                      
                      {/* Detail Pengajuan Card */}
                      <div className='bg-white rounded-3 p-3 mb-4'>
                        <div className='row g-3'>
                          <div className='col-md-3 col-6'>
                            <div className='d-flex align-items-center'>
                              <div className='rounded-circle p-2 me-2' style={{background: '#d4edda'}}>
                                <i className='bi bi-hash text-success'></i>
                              </div>
                              <div>
                                <small className='text-muted d-block'>ID Pengajuan</small>
                                <span className='fw-bold'>{formatLaporanPengajuanId(monev.laporan.id)}</span>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-3 col-6'>
                            <div className='d-flex align-items-center'>
                              <div className='rounded-circle p-2 me-2' style={{background: '#d4edda'}}>
                                <i className='bi bi-calendar-plus text-success'></i>
                              </div>
                              <div>
                                <small className='text-muted d-block'>Tanggal Submit</small>
                                <span className='fw-bold'>{formatLaporanTanggalId(monev.laporan.created_at)}</span>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-3 col-6'>
                            <div className='d-flex align-items-center'>
                              <div className='rounded-circle p-2 me-2' style={{background: '#d4edda'}}>
                                <i className='bi bi-person-check text-success'></i>
                              </div>
                              <div>
                                <small className='text-muted d-block'>Direview Oleh</small>
                                <span className='fw-bold'>{monev.laporan.reviewed_by || monev.laporan.reviewer_name || 'Admin'}</span>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-3 col-6'>
                            <div className='d-flex align-items-center'>
                              <div className='rounded-circle p-2 me-2' style={{background: '#d4edda'}}>
                                <i className='bi bi-calendar-check text-success'></i>
                              </div>
                              <div>
                                <small className='text-muted d-block'>Tanggal Disetujui</small>
                                <span className='fw-bold'>{formatLaporanTanggalId(monev.laporan.reviewed_at)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Judul */}
                      <div className='mb-3'>
                        <label className='text-muted small'>Judul Laporan</label>
                        <div className='fw-bold fs-5' style={{color: '#28a745'}}>{monev.laporan.judul || monev.laporan.title || '-'}</div>
                      </div>

                      {/* Deskripsi */}
                      {(monev.laporan.deskripsi || monev.laporan.description) && (
                        <div className='bg-white rounded p-4 mb-3 border-start border-success border-4'>
                          <label className='text-muted small mb-2 d-block'>Deskripsi Kegiatan</label>
                          <p className='mb-0' style={{lineHeight: '1.8'}}>{monev.laporan.deskripsi || monev.laporan.description}</p>
                        </div>
                      )}

                      {/* Link */}
                      {(monev.laporan.link || monev.laporan.url) && (
                        <div className='mb-3'>
                          <label className='text-muted small'>Link Dokumentasi</label>
                          <div>
                            <a href={monev.laporan.link || monev.laporan.url} target='_blank' rel='noopener noreferrer' className='d-inline-flex align-items-center text-primary'>
                              <i className='bi bi-link-45deg me-1'></i>
                              {monev.laporan.link || monev.laporan.url}
                            </a>
                          </div>
                        </div>
                      )}

                      {/* Files */}
                      {((monev.laporan.files && monev.laporan.files.length > 0) || (monev.laporan.attachments && monev.laporan.attachments.length > 0)) && (
                        <div className='mt-3'>
                          <label className='text-muted small mb-2 d-block'>File Lampiran ({(monev.laporan.files || monev.laporan.attachments || []).length} file)</label>
                          <div className='d-flex flex-wrap gap-2'>
                            {(monev.laporan.files || monev.laporan.attachments || []).map((file) => (
                              <div key={file.id} className='d-flex align-items-center justify-content-between p-3 bg-white rounded'>
                                <div className='d-flex align-items-center'>
                                  <div className='rounded p-2 me-3' style={{background: '#d4edda'}}>
                                    <i className='bi bi-file-earmark fs-4 text-success'></i>
                                  </div>
                                  <div>
                                    <div className='fw-bold'>{file.file_name}</div>
                                    <small className='text-muted'>Lampiran</small>
                                  </div>
                                </div>
                                <a href={file.file_url || file.file_path} target='_blank' rel='noopener noreferrer' className='btn btn-outline-success btn-sm ms-3'>
                                  <i className='bi bi-download me-1'></i>Download
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : monev.status == 'pending_review' && monev.laporan ? (
                    /* PENDING REVIEW - Menunggu review admin */
                    <div className='p-4 rounded-3' style={{background: '#cfe2ff'}}>
                      <h6 className='fw-bold mb-3 d-flex align-items-center'>
                        <i className='bi bi-hourglass-split me-2 text-primary'></i>
                        Menunggu Review Admin
                      </h6>
                      
                      <div className='alert alert-primary py-2 mb-3'>
                        <small>
                          <i className='bi bi-info-circle me-1'></i>
                          Laporan Anda sedang dalam proses review oleh admin. Mohon tunggu hasil review.
                        </small>
                      </div>
                      
                      {/* Detail Pengajuan Card */}
                      <div className='bg-white rounded-3 p-3 mb-4'>
                        <div className='row g-3'>
                          <div className='col-md-4 col-6'>
                            <div className='d-flex align-items-center'>
                              <div className='rounded-circle p-2 me-2' style={{background: '#cfe2ff'}}>
                                <i className='bi bi-hash text-primary'></i>
                              </div>
                              <div>
                                <small className='text-muted d-block'>ID Pengajuan</small>
                                <span className='fw-bold'>{formatLaporanPengajuanId(monev.laporan.id)}</span>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-4 col-6'>
                            <div className='d-flex align-items-center'>
                              <div className='rounded-circle p-2 me-2' style={{background: '#cfe2ff'}}>
                                <i className='bi bi-calendar-plus text-primary'></i>
                              </div>
                              <div>
                                <small className='text-muted d-block'>Tanggal Submit</small>
                                <span className='fw-bold'>{formatLaporanTanggalId(monev.laporan.created_at)}</span>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-4 col-6'>
                            <div className='d-flex align-items-center'>
                              <div className='rounded-circle p-2 me-2' style={{background: '#cfe2ff'}}>
                                <i className='bi bi-clock-history text-primary'></i>
                              </div>
                              <div>
                                <small className='text-muted d-block'>Status</small>
                                <span className='fw-bold text-primary'>
                                  {getLaporanReviewStatusLabel(monev.laporan)}
                                </span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Judul */}
                      <div className='mb-3'>
                        <label className='text-muted small'>Judul Laporan</label>
                        <div className='fw-bold fs-5' style={{color: '#0d6efd'}}>{monev.laporan.judul || monev.laporan.title || '-'}</div>
                      </div>

                      {/* Deskripsi */}
                      {(monev.laporan.deskripsi || monev.laporan.description) && (
                        <div className='bg-white rounded p-4 mb-3 border-start border-primary border-4'>
                          <label className='text-muted small mb-2 d-block'>Deskripsi Kegiatan</label>
                          <p className='mb-0' style={{lineHeight: '1.8'}}>{monev.laporan.deskripsi || monev.laporan.description}</p>
                        </div>
                      )}

                      {/* Link */}
                      {(monev.laporan.link || monev.laporan.url) && (
                        <div className='mb-3'>
                          <label className='text-muted small'>Link Dokumentasi</label>
                          <div>
                            <a href={monev.laporan.link || monev.laporan.url} target='_blank' rel='noopener noreferrer' className='d-inline-flex align-items-center text-primary'>
                              <i className='bi bi-link-45deg me-1'></i>
                              {monev.laporan.link || monev.laporan.url}
                            </a>
                          </div>
                        </div>
                      )}

                      {/* Files */}
                      {((monev.laporan.files && monev.laporan.files.length > 0) || (monev.laporan.attachments && monev.laporan.attachments.length > 0)) && (
                        <div className='mt-3'>
                          <label className='text-muted small mb-2 d-block'>File Lampiran ({(monev.laporan.files || monev.laporan.attachments || []).length} file)</label>
                          <div className='d-flex flex-wrap gap-2'>
                            {(monev.laporan.files || monev.laporan.attachments || []).map((file) => (
                              <div key={file.id} className='d-flex align-items-center justify-content-between p-3 bg-white rounded'>
                                <div className='d-flex align-items-center'>
                                  <div className='rounded p-2 me-3' style={{background: '#cfe2ff'}}>
                                    <i className='bi bi-file-earmark fs-4 text-primary'></i>
                                  </div>
                                  <div>
                                    <div className='fw-bold'>{file.file_name}</div>
                                    <small className='text-muted'>Lampiran</small>
                                  </div>
                                </div>
                                <a href={file.file_url || file.file_path} target='_blank' rel='noopener noreferrer' className='btn btn-outline-primary btn-sm ms-3'>
                                  <i className='bi bi-download me-1'></i>Download
                                </a>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  ) : monev.status == 'revision' && monev.laporan ? (
                    /* REVISION - Perlu revisi, tampilkan catatan dan form edit */
                    <div className='p-4 rounded-3' style={{background: '#f8d7da'}}>
                      <h6 className='fw-bold mb-3 d-flex align-items-center'>
                        <i className='bi bi-exclamation-triangle-fill me-2 text-danger'></i>
                        Perlu Revisi
                      </h6>
                      
                      {/* Detail Pengajuan Card */}
                      <div className='bg-white rounded-3 p-3 mb-4'>
                        <div className='row g-3'>
                          <div className='col-md-3 col-6'>
                            <div className='d-flex align-items-center'>
                              <div className='rounded-circle p-2 me-2' style={{background: '#f8d7da'}}>
                                <i className='bi bi-hash text-danger'></i>
                              </div>
                              <div>
                                <small className='text-muted d-block'>ID Pengajuan</small>
                                <span className='fw-bold'>{formatLaporanPengajuanId(monev.laporan.id)}</span>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-3 col-6'>
                            <div className='d-flex align-items-center'>
                              <div className='rounded-circle p-2 me-2' style={{background: '#f8d7da'}}>
                                <i className='bi bi-calendar-plus text-danger'></i>
                              </div>
                              <div>
                                <small className='text-muted d-block'>Tanggal Submit</small>
                                <span className='fw-bold'>{formatLaporanTanggalId(monev.laporan.created_at)}</span>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-3 col-6'>
                            <div className='d-flex align-items-center'>
                              <div className='rounded-circle p-2 me-2' style={{background: '#f8d7da'}}>
                                <i className='bi bi-person-x text-danger'></i>
                              </div>
                              <div>
                                <small className='text-muted d-block'>Direview Oleh</small>
                                <span className='fw-bold'>{monev.laporan.reviewed_by || monev.laporan.reviewer_name || 'Admin'}</span>
                              </div>
                            </div>
                          </div>
                          <div className='col-md-3 col-6'>
                            <div className='d-flex align-items-center'>
                              <div className='rounded-circle p-2 me-2' style={{background: '#f8d7da'}}>
                                <i className='bi bi-calendar-x text-danger'></i>
                              </div>
                              <div>
                                <small className='text-muted d-block'>Tanggal Revisi</small>
                                <span className='fw-bold'>{formatLaporanTanggalId(monev.laporan.reviewed_at)}</span>
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                      
                      {/* Catatan Admin */}
                      {(monev.laporan.catatan || monev.laporan.catatan_revisi || monev.laporan.review_note) && (
                        <div className='alert alert-danger mb-4'>
                          <div className='d-flex align-items-start'>
                            <i className='bi bi-chat-left-text fs-4 me-3 text-danger'></i>
                            <div>
                              <div className='fw-bold mb-1'>Catatan Admin:</div>
                              <p className='mb-0'>{monev.laporan.catatan || monev.laporan.catatan_revisi || monev.laporan.review_note}</p>
                            </div>
                          </div>
                        </div>
                      )}
                      
                      {/* Form untuk revisi */}
                      {editingLaporanId == monev.laporan.id ? (
                        <div className='p-4 bg-white rounded-3'>
                          {/* Judul */}
                          <div className='mb-4'>
                            <label className='form-label fw-bold'>
                              <i className='bi bi-bookmark me-2' style={{color: '#F95E2F'}}></i>
                              Judul Laporan <span className='text-danger'>*</span>
                            </label>
                            <input
                              type='text'
                              className='form-control form-control-lg'
                              placeholder='Masukkan judul laporan...'
                              value={formData.judul}
                              onChange={(e) => setFormData({...formData, judul: e.target.value})}
                              style={{borderRadius: '10px'}}
                            />
                          </div>

                          {/* Deskripsi */}
                          <div className='mb-4'>
                            <label className='form-label fw-bold'>
                              <i className='bi bi-pencil-square me-2' style={{color: '#F95E2F'}}></i>
                              Deskripsi Kegiatan <span className='text-danger'>*</span>
                            </label>
                            <textarea
                              className='form-control'
                              rows={6}
                              placeholder='Tuliskan deskripsi kegiatan...'
                              value={formData.deskripsi}
                              onChange={(e) => setFormData({...formData, deskripsi: e.target.value})}
                              style={{borderRadius: '10px', lineHeight: '1.8'}}
                            ></textarea>
                            <small className={formData.deskripsi.trim().length < 2000 ? 'text-danger' : 'text-success'}>
                              {formData.deskripsi.trim().length} / 2000 karakter minimum
                            </small>
                          </div>

                          {/* Link */}
                          <div className='mb-4'>
                            <label className='form-label fw-bold'>
                              <i className='bi bi-link-45deg me-2' style={{color: '#F95E2F'}}></i>
                              Link Dokumentasi <span className='text-danger'>*</span>
                            </label>
                            <input
                              type='url'
                              className='form-control'
                              placeholder='https://...'
                              value={formData.link}
                              onChange={(e) => setFormData({...formData, link: e.target.value})}
                              style={{borderRadius: '10px'}}
                            />
                          </div>
                          
                          {/* Files */}
                          <div className='mb-4'>
                            <label className='form-label fw-bold'>
                              <i className='bi bi-cloud-upload me-2' style={{color: '#F95E2F'}}></i>
                              Upload File Tambahan
                            </label>
                            <div 
                              className='p-4 bg-light rounded-3 text-center' 
                              style={{borderStyle: 'dashed', borderWidth: '2px', borderColor: '#F95E2F'}}
                            >
                              <input
                                type='file'
                                className='d-none'
                                ref={fileInputRef}
                                multiple
                                accept='.pdf,.jpg,.jpeg,.png,.doc,.docx'
                                onChange={handleFileChange}
                              />
                              <div style={{cursor: 'pointer'}} onClick={() => fileInputRef.current?.click()}>
                                <i className='bi bi-cloud-arrow-up fs-1 d-block mb-2' style={{color: '#F95E2F'}}></i>
                                <div className='fw-bold mb-1'>Klik untuk upload file</div>
                                <small className='text-muted'>Format: PDF, JPG, PNG, DOC</small>
                              </div>
                            </div>
                            {formData.files.length > 0 && (
                              <div className='mt-3'>
                                {formData.files.map((file, index) => (
                                  <div key={index} className='d-flex align-items-center justify-content-between p-2 bg-light rounded mb-2'>
                                    <div className='d-flex align-items-center'>
                                      <i className='bi bi-file-earmark me-2' style={{color: '#F95E2F'}}></i>
                                      <span className='small'>{file.name}</span>
                                    </div>
                                    <button type='button' className='btn btn-sm btn-outline-danger' onClick={() => removeFile(index)}>
                                      <i className='bi bi-x'></i>
                                    </button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          
                          <div className='d-flex gap-2'>
                            <button
                              className='btn btn-pejuang btn-lg'
                              onClick={() => handleSubmit(monev.minggu_ke, monev.laporan?.id)}
                              disabled={!formData.judul.trim() || submitting}
                            >
                              {submitting ? (
                                <>
                                  <span className='spinner-border spinner-border-sm me-2'></span>
                                  Menyimpan...
                                </>
                              ) : (
                                <>
                                  <i className='bi bi-send me-2'></i>
                                  Submit Revisi
                                </>
                              )}
                            </button>
                            <button
                              className='btn btn-outline-secondary btn-lg'
                              onClick={() => {
                                setEditingLaporanId(null)
                                setFormData({judul: '', deskripsi: '', link: '', files: []})
                              }}
                            >
                              Batal
                            </button>
                          </div>
                        </div>
                      ) : (
                        /* Tampilkan data lama dan tombol edit */
                        <>
                          {/* Judul */}
                          <div className='mb-3'>
                            <label className='text-muted small'>Judul Laporan Sebelumnya</label>
                            <div className='fw-bold fs-5 text-danger'>{monev.laporan.judul || monev.laporan.title || '-'}</div>
                          </div>

                          {/* Deskripsi */}
                          {(monev.laporan.deskripsi || monev.laporan.description) && (
                            <div className='bg-white rounded p-4 mb-3 border-start border-danger border-4'>
                              <label className='text-muted small mb-2 d-block'>Deskripsi Kegiatan</label>
                              <p className='mb-0' style={{lineHeight: '1.8'}}>{monev.laporan.deskripsi || monev.laporan.description}</p>
                            </div>
                          )}

                          {/* Link */}
                          {(monev.laporan.link || monev.laporan.url) && (
                            <div className='mb-3'>
                              <label className='text-muted small'>Link Dokumentasi</label>
                              <div>
                                <a href={monev.laporan.link || monev.laporan.url} target='_blank' rel='noopener noreferrer' className='d-inline-flex align-items-center text-primary'>
                                  <i className='bi bi-link-45deg me-1'></i>
                                  {monev.laporan.link || monev.laporan.url}
                                </a>
                              </div>
                            </div>
                          )}

                          {/* Files */}
                          {((monev.laporan.files && monev.laporan.files.length > 0) || (monev.laporan.attachments && monev.laporan.attachments.length > 0)) && (
                            <div className='mb-3'>
                              <label className='text-muted small mb-2 d-block'>File Lampiran ({(monev.laporan.files || monev.laporan.attachments || []).length} file)</label>
                              <div className='d-flex flex-wrap gap-2'>
                                {(monev.laporan.files || monev.laporan.attachments || []).map((file) => (
                                  <div key={file.id} className='d-flex align-items-center justify-content-between p-3 bg-white rounded'>
                                    <div className='d-flex align-items-center'>
                                      <div className='rounded p-2 me-3' style={{background: '#f8d7da'}}>
                                        <i className='bi bi-file-earmark fs-4 text-danger'></i>
                                      </div>
                                      <div>
                                        <div className='fw-bold'>{file.file_name}</div>
                                        <small className='text-muted'>Lampiran</small>
                                      </div>
                                    </div>
                                    <a href={file.file_url || file.file_path} target='_blank' rel='noopener noreferrer' className='btn btn-outline-danger btn-sm ms-3'>
                                      <i className='bi bi-download me-1'></i>Download
                                    </a>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          <button
                            className='btn btn-danger btn-lg mt-3'
                            onClick={() => loadFormForRevision(monev.laporan!)}
                          >
                            <i className='bi bi-pencil me-2'></i>
                            Edit & Submit Revisi
                          </button>
                        </>
                      )}
                    </div>
                  ) : monev.status == 'active' ? (
                    // Form for active week
                    <div className='p-4 rounded-3' style={{background: '#FFF3EE'}}>
                      {/* Judul */}
                      <div className='mb-4'>
                        <label className='form-label fw-bold'>
                          <i className='bi bi-bookmark me-2' style={{color: '#F95E2F'}}></i>
                          Judul Laporan <span className='text-danger'>*</span>
                        </label>
                        <input
                          type='text'
                          className='form-control form-control-lg'
                          placeholder='Masukkan judul laporan kegiatan minggu ini...'
                          value={formData.judul}
                          onChange={(e) => setFormData({...formData, judul: e.target.value})}
                          style={{borderRadius: '10px'}}
                        />
                      </div>

                      {/* Deskripsi */}
                      <div className='mb-4'>
                        <label className='form-label fw-bold'>
                          <i className='bi bi-pencil-square me-2' style={{color: '#F95E2F'}}></i>
                          Deskripsi Kegiatan <span className='text-danger'>*</span>
                        </label>
                        <textarea
                          className='form-control'
                          rows={6}
                          placeholder='Tuliskan deskripsi kegiatan minggu ini. Jelaskan kegiatan apa saja yang sudah dilakukan, kendala yang dihadapi, dan pencapaian yang diraih...'
                          value={formData.deskripsi}
                          onChange={(e) => setFormData({...formData, deskripsi: e.target.value})}
                          style={{borderRadius: '10px', lineHeight: '1.8'}}
                        ></textarea>
                        <small className={formData.deskripsi.trim().length < 2000 ? 'text-danger' : 'text-success'}>
                          {formData.deskripsi.trim().length} / 2000 karakter minimum
                        </small>
                      </div>

                      {/* Link */}
                      <div className='mb-4'>
                        <label className='form-label fw-bold'>
                          <i className='bi bi-link-45deg me-2' style={{color: '#F95E2F'}}></i>
                          Link Dokumentasi <span className='text-danger'>*</span>
                        </label>
                        <input
                          type='url'
                          className='form-control'
                          placeholder='https://drive.google.com/... atau https://youtube.com/...'
                          value={formData.link}
                          onChange={(e) => setFormData({...formData, link: e.target.value})}
                          style={{borderRadius: '10px'}}
                        />
                        <small className='text-muted'>Masukkan link video atau dokumen pendukung</small>
                      </div>
                      
                      {/* Files */}
                      <div className='mb-4'>
                        <label className='form-label fw-bold'>
                          <i className='bi bi-cloud-upload me-2' style={{color: '#F95E2F'}}></i>
                          Upload File Lampiran
                        </label>
                        <div 
                          className='p-4 bg-white rounded-3 text-center' 
                          style={{borderStyle: 'dashed', borderWidth: '2px', borderColor: '#F95E2F'}}
                        >
                          <input
                            type='file'
                            className='d-none'
                            ref={fileInputRef}
                            multiple
                            accept='.pdf,.jpg,.jpeg,.png,.doc,.docx'
                            onChange={handleFileChange}
                          />
                          <div 
                            style={{cursor: 'pointer'}}
                            onClick={() => fileInputRef.current?.click()}
                          >
                            <i className='bi bi-cloud-arrow-up fs-1 d-block mb-2' style={{color: '#F95E2F'}}></i>
                            <div className='fw-bold mb-1'>Klik untuk upload file</div>
                            <small className='text-muted'>Format: PDF, JPG, PNG, DOC. Maksimal 5MB per file</small>
                          </div>
                        </div>

                        {/* File list */}
                        {formData.files.length > 0 && (
                          <div className='mt-3'>
                            {formData.files.map((file, index) => (
                              <div 
                                key={index} 
                                className='d-flex align-items-center justify-content-between p-2 bg-white rounded mb-2'
                              >
                                <div className='d-flex align-items-center'>
                                  <i className='bi bi-file-earmark me-2' style={{color: '#F95E2F'}}></i>
                                  <span className='small'>{file.name}</span>
                                </div>
                                <button 
                                  type='button'
                                  className='btn btn-sm btn-outline-danger'
                                  onClick={() => removeFile(index)}
                                >
                                  <i className='bi bi-x'></i>
                                </button>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                      
                      <button
                        className='btn btn-pejuang btn-lg'
                        onClick={() => handleSubmit(monev.minggu_ke)}
                        disabled={!formData.judul.trim() || submitting}
                      >
                        {submitting ? (
                          <>
                            <span className='spinner-border spinner-border-sm me-2'></span>
                            Menyimpan...
                          </>
                        ) : (
                          <>
                            <i className='bi bi-send me-2'></i>
                            Submit Laporan Minggu {monev.minggu_ke}
                          </>
                        )}
                      </button>
                    </div>
                  ) : null}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Info Card */}
      <div className='pejuang-card mt-5'>
        <div className='card-body'>
          <div className='row align-items-center'>
            <div className='col-md-8'>
              <div className='d-flex align-items-start'>
                <div className='rounded-circle p-3 me-3' style={{background: '#e3f2fd'}}>
                  <i className='bi bi-lightbulb fs-4 text-primary'></i>
                </div>
                <div>
                  <h6 className='fw-bold mb-2'>Tips Penulisan Laporan</h6>
                  <ul className='mb-0 text-muted'>
                    <li>Buat judul yang ringkas dan jelas</li>
                    <li>Deskripsikan kegiatan dengan terstruktur</li>
                    <li>Sertakan link video atau dokumentasi kegiatan</li>
                    <li>Upload file pendukung jika diperlukan</li>
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

const Monev: FC = () => {
  return (
    <>
      <PageTitle
        breadcrumbs={[
          {title: 'PANA', path: '/pana-beranda', isSeparator: false, isActive: false},
          {title: '/', path: '', isSeparator: false, isActive: false},
        ]}
      >
        Laporan Kegiatan
      </PageTitle>
      <MonevPage />
    </>
  )
}

export default Monev
